import { useState } from "react";
import { Button, Grid, TextField, Typography, CircularProgress } from "@mui/material";
import { theme } from "../../../theme";
import { fetchWithTimeout } from "../../../utils/fetchWithTimeout";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../../utils/FireBaseConfigurations/config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const ForgotPasswordForm = ({ onBack, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Use Firebase's password reset for email/password users
      // This updates both Firebase and we'll sync to backend on next login
      await sendPasswordResetEmail(auth, email.toLowerCase(), {
        url: `${window.location.origin}/__/auth/action`, // Redirect back to home page after reset
        handleCodeInApp: false,
      });

      setSuccess(true);
      if (onSuccess) onSuccess("Password reset email sent. Please check your inbox.");
      
      // Close dialog after 3 seconds
      setTimeout(() => {
        if (onBack) onBack();
      }, 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      
      // Handle Firebase errors
      if (err.code === "auth/user-not-found") {
        setError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else {
        setError("Failed to send reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      sx={{
        alignItems: "center",
        pt: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 2.5 },
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.5, sm: 2.5 },
        width: "100%",
        maxWidth: { xs: "100%", sm: 380, md: 420, lg: 440 },
        mx: "auto",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: theme.palette.primary.main,
          textAlign: "center",
          mb: 2,
        }}
      >
        Forgot Password?
      </Typography>

      {!success ? (
        <>
          <Typography variant="body2" sx={{ color: "#464545", mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          <Typography variant="body2" sx={{ color: "#464545" }} gutterBottom>
            Email
          </Typography>
          <TextField
            type="email"
            variant="outlined"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
          />

          {error && (
            <Typography variant="caption" color="error" textAlign="center" display="block" mb={1}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            size="small"
            disabled={loading || !email}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              padding: "8px",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#333" },
              mb: 2,
            }}
            onClick={handleSubmit}
          >
            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Send Reset Link"}
          </Button>

          <Button
            variant="text"
            fullWidth
            size="small"
            onClick={onBack}
            sx={{
              textTransform: "none",
              color: "#464545",
            }}
          >
            Back to Sign In
          </Button>
        </>
      ) : (
        <Grid textAlign="center">
          <Typography variant="body1" sx={{ color: theme.palette.primary.main, mb: 2 }}>
            ✓ Reset link sent!
          </Typography>
          <Typography variant="body2" sx={{ color: "#464545" }}>
            Please check your email inbox for the password reset link.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default ForgotPasswordForm;
